/**
 * AgentMentionAutocomplete - Dropdown for @mention agent selection
 *
 * Shows when user types @ in the chat input.
 * Supports fuzzy matching, keyboard navigation, and agent selection.
 *
 * Uses a React portal to render at the document body level so it is never
 * clipped by ancestor overflow-hidden containers (e.g. the WelcomeScreen).
 */

import { useEffect, useMemo, useRef, useState, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { useTranslation } from '@/i18n'
import type { AgentId } from '../../types'
import { AGENTS, AGENT_COLORS } from '../../constants'

export interface AgentOption {
  id: AgentId
  name: string
  label: string
  description: string
  mentionName: string
  color: 'blue' | 'green' | 'amber' | 'purple'
  avatarUrl?: string
}

interface AgentMentionAutocompleteProps {
  isVisible: boolean
  selectedIndex: number
  filterQuery: string
  onSelect: (agent: AgentOption) => void
  onClose: () => void
  /** If provided, only show agents whose chatV2 ID is in this set */
  availableAgentIds?: AgentId[]
  /** Anchor element used to compute portal position (typically the ChatInput wrapper) */
  anchorRef?: React.RefObject<HTMLElement | null>
}

// Convert AGENTS to array for iteration
const AGENT_OPTIONS: AgentOption[] = Object.values(AGENTS).map((agent) => ({
  id: agent.id,
  name: agent.name,
  label: agent.label,
  description: agent.description,
  mentionName: agent.mentionName,
  color: agent.color,
  avatarUrl: agent.avatarUrl,
}))

/**
 * Fuzzy matching function for filtering agents
 * Matches if query characters appear in order in the text
 */
const fuzzyMatch = (query: string, text: string): boolean => {
  const queryLower = query.toLowerCase()
  const textLower = text.toLowerCase()

  // Exact substring match
  if (textLower.includes(queryLower)) return true

  // Fuzzy match - check if all query characters appear in order
  let textIndex = 0
  for (let i = 0; i < queryLower.length; i++) {
    const charIndex = textLower.indexOf(queryLower[i], textIndex)
    if (charIndex === -1) return false
    textIndex = charIndex + 1
  }
  return true
}

/**
 * Filter agents based on search query and optional availability list
 */
export function filterAgents(query: string, availableIds?: AgentId[]): AgentOption[] {
  const baseAgents = availableIds
    ? AGENT_OPTIONS.filter((a) => availableIds.includes(a.id))
    : AGENT_OPTIONS

  if (!query) return baseAgents

  const queryLower = query.toLowerCase()

  return baseAgents.filter((agent) => {
    if (
      agent.id.toLowerCase().includes(queryLower) ||
      agent.mentionName.toLowerCase().includes(queryLower)
    ) {
      return true
    }

    return fuzzyMatch(query, agent.label)
  })
}

export function AgentMentionAutocomplete({
  isVisible,
  selectedIndex,
  filterQuery,
  onSelect,
  onClose,
  availableAgentIds,
  anchorRef,
}: AgentMentionAutocompleteProps) {
  const { t, i18n } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 400 })

  // Filter agents based on availability and query
  const filteredAgents = filterAgents(filterQuery, availableAgentIds)

  // Base available agents (without query filter) for the "try typing" hint
  const baseAgents = useMemo(() => filterAgents('', availableAgentIds), [availableAgentIds])

  // Build dynamic keywords hint from available agents
  const tryKeywordsHint = useMemo(() => {
    if (baseAgents.length === 0) return ''
    const keywords = baseAgents.map(
      (agent) => `"${t(`chatv2:agents.${agent.id}.search_keyword`)}"`,
    )
    if (keywords.length === 1) return keywords[0]
    try {
      return new Intl.ListFormat(i18n.language, { style: 'long', type: 'disjunction' }).format(keywords)
    } catch {
      return keywords.join(', ')
    }
  }, [baseAgents, t, i18n.language])

  // Compute position relative to the anchor element
  useLayoutEffect(() => {
    if (!isVisible || !anchorRef?.current) return
    const rect = anchorRef.current.getBoundingClientRect()
    setPos({
      top: rect.top + window.scrollY,
      left: rect.left + window.scrollX,
      width: Math.min(rect.width, 448), // max-w-md ≈ 28rem = 448px
    })
  }, [isVisible, anchorRef])

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose()
      }
    }

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isVisible, onClose])

  if (!isVisible) return null

  const dropdown = (
    <div
      ref={containerRef}
      className="bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-lg shadow-lg overflow-hidden"
      style={
        anchorRef
          ? { position: 'fixed', top: pos.top - 8, left: pos.left, width: pos.width, transform: 'translateY(-100%)', zIndex: 9999 }
          : undefined
      }
    >
      {/* Header with hint */}
      <div className="px-3 py-2 bg-gray-50 dark:bg-dark-muted border-b border-gray-200 dark:border-dark-border">
        <p className="text-xs text-gray-600 dark:text-gray-400 font-medium">
          {filterQuery
            ? t('chatv2:agents.mention_autocomplete_filtering', { query: filterQuery })
            : t('chatv2:agents.mention_autocomplete_hint')}
        </p>
      </div>

      {/* Agent list */}
      <div className="py-1 max-h-64 overflow-y-auto">
        {filteredAgents.length === 0 ? (
          <div className="px-4 py-6 text-center">
            <p className="text-sm text-gray-500 dark:text-dark-text-muted">
              {t('chatv2:agents.mention_autocomplete_no_results', { query: filterQuery })}
            </p>
            {tryKeywordsHint && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {t('chatv2:agents.mention_autocomplete_try', { keywords: tryKeywordsHint })}
              </p>
            )}
          </div>
        ) : (
          filteredAgents.map((agent, index) => {
            const colors = AGENT_COLORS[agent.color]
            const isSelected = selectedIndex === index

            return (
              <button
                key={agent.id}
                onClick={() => onSelect(agent)}
                className={clsx(
                  'w-full px-4 py-3 flex items-center gap-3 transition-colors',
                  isSelected ? 'bg-gray-100 dark:bg-dark-accent' : 'hover:bg-gray-50 dark:hover:bg-dark-muted'
                )}
              >
                {/* Agent avatar */}
                <div className="relative w-8 h-8 rounded-full flex-shrink-0">
                  {agent.avatarUrl ? (
                    <>
                      <img
                        src={agent.avatarUrl}
                        alt={agent.label}
                        className="w-8 h-8 rounded-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          const fallback = target.nextElementSibling as HTMLElement
                          if (fallback) fallback.classList.remove('hidden')
                        }}
                      />
                      {/* Fallback letter avatar */}
                      <div
                        className={clsx(
                          'hidden w-8 h-8 rounded-full flex items-center justify-center',
                          'font-mono font-semibold text-sm border absolute inset-0',
                          colors.bg,
                          colors.text,
                          colors.border
                        )}
                      >
                        {agent.label.charAt(0)}
                      </div>
                    </>
                  ) : (
                    <div
                      className={clsx(
                        'w-8 h-8 rounded-full flex items-center justify-center',
                        'font-mono font-semibold text-sm border',
                        colors.bg,
                        colors.text,
                        colors.border
                      )}
                    >
                      {agent.label.charAt(0)}
                    </div>
                  )}
                </div>

                {/* Agent info */}
                <div className="flex flex-col items-start flex-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-dark-text">
                    {agent.label}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-dark-text-muted">
                    {agent.mentionName}
                  </span>
                </div>

                {/* Enter hint for selected item */}
                {isSelected && (
                  <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                    <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-dark-border rounded text-[10px] font-mono">
                      ↵
                    </kbd>
                  </div>
                )}
              </button>
            )
          })
        )}
      </div>

      {/* Keyboard hints footer */}
      <div className="px-3 py-2 bg-gray-50 dark:bg-dark-muted border-t border-gray-200 dark:border-dark-border flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-dark-text-muted">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white dark:bg-dark-card border border-gray-300 dark:border-gray-600 rounded text-[10px] font-mono">
              ↑↓
            </kbd>
            <span>{t('chatv2:keyboard_shortcuts.navigate', { defaultValue: 'Navigate' })}</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white dark:bg-dark-card border border-gray-300 dark:border-gray-600 rounded text-[10px] font-mono">
              ↵
            </kbd>
            <span>{t('chatv2:keyboard_shortcuts.select', { defaultValue: 'Select' })}</span>
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-white dark:bg-dark-card border border-gray-300 dark:border-gray-600 rounded text-[10px] font-mono">
              Esc
            </kbd>
            <span>{t('chatv2:keyboard_shortcuts.close', { defaultValue: 'Close' })}</span>
          </span>
        </div>
      </div>
    </div>
  )

  // Use portal when anchor is provided (escapes overflow-hidden ancestors)
  if (anchorRef) {
    return createPortal(dropdown, document.body)
  }

  return dropdown
}

export default AgentMentionAutocomplete
